import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Divider, InputAdornment, InputBase, alpha, styled } from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink, generatePath } from 'react-router';

import appStrings from '@/assets/constants/appStrings-no.ts';
import routes from '@/assets/constants/routes-no.ts';

import SectionTitle from '../SectionTitle/index.ts';

// Inlined data from data.json
const faqData = [
  {
    categories: {
      category: [
        { id: 5, name: 'Public' },
        { id: 3, name: 'Refinansiering' },
      ],
    },
    id: 55,
    slug: 'hva-er-et-nedbetalingslan',
    title: 'Hva er et nedbetalingslån?',
  },
  {
    categories: {
      category: [
        { id: 5, name: 'Public' },
        { id: 3, name: 'Refinansiering' },
      ],
    },
    id: 47,
    slug: 'hvem-kan-soke-om-et-forbrukslan-hos-scoopr',
    title: 'Hvem kan søke om et forbrukslån hos Scoopr?',
  },
  {
    categories: {
      category: [
        { id: 5, name: 'Public' },
        { id: 3, name: 'Refinansiering' },
      ],
    },
    id: 52,
    slug: 'kan-jeg-endre-lanebelopet-eller-lopetiden',
    title: 'Kan jeg endre lånebeløpet eller løpetiden?',
  },
  {
    categories: {
      category: [
        { id: 5, name: 'Public' },
        { id: 3, name: 'Refinansiering' },
      ],
    },
    id: 50,
    slug: 'kan-jeg-ha-med-medsoker-i-soknaden',
    title: 'Kan jeg ha med medsøker i søknaden?',
  },
  {
    categories: {
      category: [
        { id: 5, name: 'Public' },
        { id: 3, name: 'Refinansiering' },
      ],
    },
    id: 49,
    slug: 'hvordan-soker-jeg-pa-refinansiering-hos-scoopr',
    title: 'Hvordan søker jeg på refinansiering hos Scoopr?',
  },
  {
    categories: {
      category: [
        { id: 5, name: 'Public' },
        { id: 3, name: 'Refinansiering' },
      ],
    },
    id: 44,
    slug: 'samle-smalan-og-kredittkort',
    title: 'Samle smålån og kredittkort',
  },
  {
    categories: {
      category: [
        { id: 5, name: 'Public' },
        { id: 3, name: 'Refinansiering' },
      ],
    },
    id: 53,
    slug: 'hva-er-gjeldsregisteret',
    title: 'Hva er gjeldsregisteret?',
  },
  {
    categories: {
      category: [
        { id: 5, name: 'Public' },
        { id: 3, name: 'Refinansiering' },
      ],
    },
    id: 51,
    slug: 'hvor-stort-lan-kan-jeg-fa',
    title: 'Hvor stort lån kan jeg få?',
  },
  {
    categories: {
      category: [
        { id: 5, name: 'Public' },
        { id: 3, name: 'Refinansiering' },
      ],
    },
    id: 48,
    slug: 'betaler-dere-ut-min-eksisterende-gjeld',
    title: 'Betaler dere ned min eksisterende gjeld?',
  },
  {
    categories: {
      category: [
        { id: 5, name: 'Public' },
        { id: 3, name: 'Refinansiering' },
      ],
    },
    id: 46,
    slug: 'retningslinjer-for-forbrukslan',
    title: 'Retningslinjer for forbrukslån',
  },
];

const FaqContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  boxShadow: theme.shadows[24],
  padding: theme.spacing(0.5, 1.5, 0.5, 3),
  margin: theme.spacing(0, 'auto', 3.5),
  maxWidth: 700,
  borderRadius: 40,
  color: theme.palette.text.default,
  backgroundColor: theme.palette.background.paper,
  '& .MuiInputBase-input': {
    padding: theme.spacing(1.5, 0),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(2, 0),
    },
  },
}));

const StyledInputAdornment = styled(InputAdornment)(({ theme }) => ({
  margin: theme.spacing(0, -0.75, 0, 0.5),
  minHeight: 42,
  width: 42,
  minWidth: 42,
  justifyContent: 'center',
  borderRadius: '50%',
  backgroundColor: theme.palette.secondary.main,
  [theme.breakpoints.up('md')]: {
    minHeight: 52,
    width: 52,
    minWidth: 52,
    '& svg': { fontSize: '2rem' },
  },
}));

const FilterContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  margin: theme.spacing(0, 'auto', 3.5),
  maxWidth: 300,
  [theme.breakpoints.up('sm')]: {
    maxWidth: 'none',
  },
}));

const FilterButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  color: alpha(theme.palette.text.default, 0.5),
  padding: theme.spacing(0.5, 1),
  ...(isActive && {
    color: theme.palette.text.default,
    fontWeight: theme.typography.fontWeightBold,
  }),
}));

const FilterDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing('auto', 0.5),
  backgroundColor: alpha(theme.palette.text.default, 0.5),
  height: '60%',
}));

const FaqItemsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'left',
  [theme.breakpoints.up('sm')]: {
    justifyContent: 'center',
  },
}));

const FaqItem = styled(Button)(({ theme }) => ({
  margin: theme.spacing(1),
})) as typeof Button;

const categories = [
  {
    key: 1,
    label: appStrings.PRODUCTS.REFINANCING,
    value: appStrings.PRODUCTS.REFINANCING,
  },
  { key: 3, label: appStrings.FAQ.ALL_PRODUCTS, value: undefined },
];

interface FaqListProps {
  category?: 'Mobil' | 'Refinansiering';
}

const FaqList = ({ category }: FaqListProps) => {
  // category filter
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(category);

  const [searchValue, setSearchValue] = useState('');

  const preparedData =
    faqData.length && searchValue.length > 1
      ? faqData.filter(({ title }) => title.toLowerCase().includes(searchValue.toLowerCase()))
      : faqData;

  return (
    <FaqContainer>
      <SectionTitle title={appStrings.FAQ.TITLE} />
      {category ? null : (
        <FilterContainer>
          {categories.map((item, index) => (
            <Box
              key={item.label}
              sx={{
                display: 'flex',
              }}
            >
              <FilterButton
                variant="text"
                size="large"
                onClick={() => setCategoryFilter(item.value)}
                isActive={categoryFilter === item.value}
              >
                {item.label}
              </FilterButton>
              {index % 2 === 0 && <FilterDivider orientation="vertical" />}
            </Box>
          ))}
        </FilterContainer>
      )}
      <StyledInputBase
        placeholder={appStrings.FAQ.SEARCH_PLACEHOLDER}
        inputProps={{ 'aria-label': appStrings.FAQ.SEARCH_LABEL }}
        value={searchValue}
        onChange={(event) => setSearchValue(event.target.value)}
        fullWidth
        endAdornment={
          <StyledInputAdornment position="end">
            <SearchIcon />
          </StyledInputAdornment>
        }
      />
      <FaqItemsContainer>
        {preparedData.length ? (
          preparedData.map((item) => {
            const product = item.categories.category
              .find(({ name }) =>
                [appStrings.PRODUCTS.MOBILE, appStrings.PRODUCTS.REFINANCING].includes(name),
              )
              ?.name.toLowerCase();
            return (
              <FaqItem
                key={item.id}
                variant="outlined"
                color="primary"
                component={RouterLink}
                to={
                  product && item.slug
                    ? generatePath(routes.PRODUCTS_FAQ_ARTICLE, {
                        articleName: item.slug,
                      })
                    : '/'
                }
              >
                {item.title}
              </FaqItem>
            );
          })
        ) : (
          <p className="w-full text-center typography-v1-legacy-p1">
            {appStrings.FAQ.SEARCH_NO_RESULTS}
          </p>
        )}
      </FaqItemsContainer>
    </FaqContainer>
  );
};

export default FaqList;
